import { z } from "zod";

/** Every browser-visible setting: a default, a unit in the name, and what it controls (see .env.example). */
const envSchema = z.object({
  /** Simulated network latency of the in-browser mock backend, in ms. */
  VITE_MOCK_LATENCY_MS: z.coerce.number().int().min(0).default(120),
  /** Name of the mock backend's demo organisation, which holds the sample portfolio. */
  VITE_DEV_ORG_NAME: z.string().min(1).default("Northlight Studio"),
  /** Prefix of the demo organisation's project numbers such as NL-2026-014 (FR-PROJ-055). */
  VITE_DEV_ORG_PROJECT_PREFIX: z
    .string()
    .regex(/^[A-Z]{1,6}$/)
    .default("NL"),
  /** Where people write when their organisation is not activated yet (D-034). Set it to the Orthos support mailbox. */
  VITE_SUPPORT_EMAIL: z.string().email().default("abhishek@orthos.design"),
  /** Session idle timeout in minutes: 15 min to 30 days, default 7 days (FR-ADMIN-047). Organisation policy later. */
  VITE_SESSION_IDLE_MINUTES: z.coerce.number().int().min(15).max(43_200).default(10_080),
  /** Session absolute timeout in days: 1 to 90, default 30 (FR-ADMIN-047). Organisation policy later. */
  VITE_SESSION_ABSOLUTE_DAYS: z.coerce.number().int().min(1).max(90).default(30),
  /** How often an open tab re-checks its session, in seconds. */
  VITE_SESSION_CHECK_SECONDS: z.coerce.number().int().min(1).max(3_600).default(30),
  /** Identity backend: the in-browser mock, or the identity service (D-026; RFC 0001 §5). */
  VITE_IDENTITY_MODE: z.enum(["mock", "live"]).default("mock"),
  /** Path of the identity service's JSON API in live mode. */
  VITE_IDENTITY_API_PATH: z.string().startsWith("/").default("/api/identity"),
  /**
   * Projects backend: the in-browser mock, or the identity service's projects (WS-29), which needs live identity and
   * goes on with the doc service's ORTHOS_DOC_DRAWING_SCOPE=project.
   */
  VITE_PROJECTS_MODE: z.enum(["mock", "live"]).default("mock"),
  /**
   * Comments backend: the in-browser mock, or the identity service's comments (FR-COLLAB-063-071), which needs live
   * projects (a comment belongs to a project's drawing).
   */
  VITE_COMMENTS_MODE: z.enum(["mock", "live"]).default("mock"),
  /** The layer the plot boundary is drawn on (PRD 11 FR-SITE-025), created on first use. */
  VITE_SITE_BOUNDARY_LAYER: z.string().min(1).max(255).default("A-SITE-BNDY"),
  /** Drawing units in one metre for the Site drawing: 1000 for a millimetre drawing, 1 for metres. */
  VITE_SITE_DRAWING_UNITS_PER_METRE: z.coerce.number().positive().max(1_000_000).default(1_000),
  /**
   * The Site map's street basemap (PRD 11 FR-SITE-006): a Protomaps PMTiles archive read by range requests. A plain URL
   * of the archive, or the doc service's basemap route (`/api/files/v1/basemap/<file>`), which sends the browser on to
   * a presigned link in the files bucket. Unset, the Site panel shows no map.
   */
  VITE_SITE_BASEMAP_URL: z.string().min(1).optional(),
  /**
   * The basemap's label fonts, MapLibre's glyph template with `{fontstack}` and `{range}`. Unset, the map draws no
   * labels (the street and building shapes still show).
   */
  VITE_SITE_BASEMAP_GLYPHS_URL: z
    .string()
    .regex(/\{fontstack\}.*\{range\}/)
    .optional(),
  /** The basemap's icon sprite, with `{flavor}` for light or dark. Unset, the map draws no road shields or icons. */
  VITE_SITE_BASEMAP_SPRITE_URL: z.string().includes("{flavor}").optional(),
  /**
   * India's external boundary as a GeoJSON file drawn over the basemap (FR-SITE-007; Survey of India depiction). Unset,
   * none is drawn, and neither are the basemap's own boundaries. Set it only once its licence has been cleared.
   */
  VITE_SITE_INDIA_BOUNDARY_URL: z.string().min(1).optional(),
  /**
   * The Add Location box's address index (static prefix-sharded files built by `orthos-site-index`): the folder holding
   * `manifest.json`, such as the doc service's basemap route `/api/files/v1/basemap/search`. Unset, no index is used.
   */
  VITE_SITE_SEARCH_INDEX_URL: z.string().min(1).optional(),
  /**
   * Where a typed place is searched: `index` (the static files, as the person types), or `geocoder` (the identity
   * service's geocoder, when Find is pressed). Defaults to `index` when the index URL is set, else `geocoder`.
   */
  VITE_SITE_SEARCH_SOURCE: z.enum(["index", "geocoder"]).optional(),
  /** When the index finds nothing, whether pressing Find asks the geocoder (only if the service has one set up). */
  VITE_SITE_SEARCH_FALLBACK: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  /** Milliseconds the box waits after a keystroke before searching the index. */
  VITE_SITE_SEARCH_DEBOUNCE_MS: z.coerce.number().int().min(0).max(2_000).default(120),
  /** The fewest characters typed before the index is searched (shards are chosen by the first two). */
  VITE_SITE_SEARCH_MIN_CHARS: z.coerce.number().int().min(2).max(10).default(2),
  /** The most suggestions shown. */
  VITE_SITE_SEARCH_RESULTS_MAX: z.coerce.number().int().min(1).max(20).default(6),
  /** A typed start that names more index shards than this waits for another character (a busy start like "sa"). */
  VITE_SITE_SEARCH_MAX_SHARDS: z.coerce.number().int().min(1).max(200).default(12),
  /** How often an open Comments panel asks for new comments in live mode, in seconds. */
  VITE_COMMENTS_POLL_SECONDS: z.coerce.number().int().min(1).max(300).default(60),
  /**
   * Studio backend (PRD 24: stages, sign-offs, tasks, milestones, hours): the in-browser mock, or the identity
   * service's Studio records (WS-23), which need live projects (a stage belongs to a project).
   */
  VITE_STUDIO_MODE: z.enum(["mock", "live"]).default("mock"),
  /** Where drawings live: this browser, or the doc service, synced between browsers (RFC 0001 §5). */
  VITE_SYNC_MODE: z.enum(["local", "live"]).default("local"),
  /** Path of the sync WebSocket in live mode. */
  VITE_SYNC_PATH: z.string().startsWith("/").default("/sync"),
  /**
   * How long a live drawing keeps taking edits while disconnected before it turns view only, in seconds
   * (FR-COLLAB-034; NFR-PLAT-44's 5 minutes). Tests shorten it.
   */
  VITE_SYNC_OFFLINE_WINDOW_SECONDS: z.coerce.number().int().min(1).max(3_600).default(300),
  /** The most operations queued while disconnected before a live drawing turns view only (FR-COLLAB-034). */
  VITE_SYNC_OFFLINE_MAX_OPERATIONS: z.coerce.number().int().min(1).max(100_000).default(2_000),
  /** Where the drafting engine runs: a Web Worker, or the main thread as a fallback (RFC 0002 §5.5). */
  VITE_DRAFT_HOST: z.enum(["worker", "inprocess"]).default("worker"),
  /**
   * Which renderer draws the Draft canvas (RFC 0003): WebGL2 where a GPU draws it, not a software rasteriser
   * (`auto`), WebGPU (where a hardware adapter gives a device, else WebGL2), WebGL2, or Canvas 2D.
   * A GPU renderer that cannot start or loses its context falls back (WebGPU to WebGL2, WebGL2 to Canvas 2D) with a
   * console warning.
   */
  VITE_DRAFT_RENDERER: z.enum(["auto", "webgpu", "webgl2", "canvas"]).default("auto"),
  /**
   * Whether the GPU renderer draws in a render worker on OffscreenCanvases (RFC 0003 §1) where the browser can (`on`),
   * or on the page (`off`). A render worker that cannot start falls back to the page with a console warning.
   */
  VITE_DRAFT_RENDER_WORKER: z.enum(["on", "off"]).default("on"),
  /**
   * Megabytes of decoded raster images and rendered PDF pages the Draft canvas keeps (IMAGEATTACH, PDFATTACH), at 4
   * bytes a pixel; the least recently drawn are let go first. The GPU render worker holds a copy of them.
   */
  VITE_DRAFT_IMAGE_CACHE_MB: z.coerce.number().int().min(16).max(8_192).default(512),
  /**
   * The JPEG quality (0–1) a HEIC or HEIF photo is converted at in the browser before it uploads (D-036): Orthos ships
   * no HEVC decoder, so the device's own decoder opens it and the project keeps a JPEG.
   */
  VITE_HEIC_JPEG_QUALITY: z.coerce.number().min(0.1).max(1).default(0.92),
  /**
   * Where project files live (FR-PROJ-117; RFC 0002 §8.1): this browser, or the doc service. Unset, they live where
   * drawings do (`VITE_SYNC_MODE`), so a command the doc service re-executes reads the files the browser read.
   */
  VITE_FILES_MODE: z.enum(["local", "live"]).optional(),
  /** Path of the doc service's project files API in live mode. */
  VITE_FILES_API_PATH: z.string().startsWith("/").default("/api/files"),
  /** Path of the doc service's library API in live mode (WS-30). */
  VITE_LIBRARY_API_PATH: z.string().startsWith("/").default("/api/library"),
  /**
   * Where the font library's shipped stand-in fonts are served on this origin (FR-IO-041): `web/public/fonts`, which
   * the doc service also reads (ORTHOS_DOC_SHIPPED_FONTS_DIR). Drawings fetch the ones they need from here.
   */
  VITE_SHIPPED_FONTS_PATH: z.string().startsWith("/").default("/fonts"),
  /**
   * The largest request the app sends to project files, in bytes (an upload, or a drawing's DXF to save as DWG): the
   * doc service's limit (ORTHOS_DOC_FILES_*) or the host's cap on request bodies, 32 MiB on staging, whichever is less.
   */
  VITE_FILES_MAX_UPLOAD_BYTES: z.coerce.number().int().positive().default(33_554_432),
  /**
   * The largest file the Files page offers to upload in live mode, in bytes. The doc service sends uploads over its
   * ORTHOS_DOC_FILES_PRESIGN_ABOVE_BYTES straight to its bucket, past the host's request cap, and refuses anything over
   * its ORTHOS_DOC_FILES_PRESIGN_MAX_BYTES, which this should not exceed. Local mode keeps VITE_FILES_MAX_UPLOAD_BYTES.
   */
  VITE_FILES_MAX_BIG_UPLOAD_BYTES: z.coerce.number().int().positive().default(2_147_483_648),
  /** How many parts of a big upload go up at once. */
  VITE_FILES_UPLOAD_PARTS_IN_FLIGHT: z.coerce.number().int().min(1).max(16).default(3),
  /** How often a part (or the one PUT) that failed is tried again, and the wait before the first retry; it doubles. */
  VITE_FILES_UPLOAD_PART_RETRIES: z.coerce.number().int().min(0).max(10).default(3),
  VITE_FILES_UPLOAD_RETRY_DELAY_MS: z.coerce.number().int().min(0).default(1_000),
  /**
   * How long a deleted file stays in the Trash in local mode, in days, before this browser removes it. In live mode the
   * doc service decides (ORTHOS_DOC_FILES_TRASH_RETENTION_DAYS) and says when each file goes.
   */
  VITE_FILES_TRASH_RETENTION_DAYS: z.coerce.number().int().min(1).max(3_650).default(30),
  /**
   * The speed suite's hooks (`tools/bench.sh`): performance marks, frame and batch records on
   * `window.__orthosBench`, and `?bench-fixture=<entities>` to open a generated plan. Off in every real build.
   */
  VITE_BENCH: z.enum(["on", "off"]).default("off"),
  /**
   * The Draft assistant (18 FR-AI-046): off (the ASSISTANT panel says AI is not set up and sends nothing), or live
   * through the ai service, which needs live projects (AI switches are per project, FR-AI-183).
   */
  VITE_AI_MODE: z.enum(["off", "live"]).default("off"),
  /**
   * Plan intake (M1): Draft's "Plan from image" flow, which reads a plan's page with the ai service and draws it.
   * Off unless "on"; on for stage only (infra/staging/web.env), and it needs VITE_AI_MODE=live.
   */
  VITE_PLAN_INTAKE: z.enum(["off", "on"]).default("off"),
  /** Path of the ai service's JSON API in live mode. */
  VITE_AI_API_PATH: z.string().startsWith("/").default("/api/ai"),
  /** At most this many selected objects are summarised for a turn; the rest are counted only (FR-AI-091, 094). */
  VITE_AI_MAX_SELECTION_OBJECTS: z.coerce.number().int().min(0).max(500).default(40),
  /** Read-tool rounds one request may take before it stops and says so (FR-AI-018). */
  VITE_AI_MAX_TOOL_ROUNDS: z.coerce.number().int().min(0).max(25).default(4),
  /** Characters of one read tool's result sent back to the model; longer results are cut and marked. */
  VITE_AI_MAX_RESULT_CHARS: z.coerce.number().int().min(200).max(200_000).default(8_000),
  /** Proposed drawing changes one request may hold (FR-AI-018's 25 tool calls a turn). */
  VITE_AI_MAX_CHANGES: z.coerce.number().int().min(1).max(500).default(25),
  /** Earlier messages kept and sent with a request; the ai service caps it too (ORTHOS_AI_MAX_HISTORY_MESSAGES). */
  VITE_AI_MAX_HISTORY_MESSAGES: z.coerce.number().int().min(0).max(200).default(30),
  /** The last command-line lines sent with a turn, so "why did TRIM fail?" has the refusal to explain (FR-AI-160). */
  VITE_AI_COMMAND_LINES: z.coerce.number().int().min(0).max(50).default(6),
  /** Characters of the request kept in the undo step's name, "AI: <title>" (FR-AI-054). */
  VITE_AI_TITLE_CHARS: z.coerce.number().int().min(10).max(200).default(60),
  /** Words a line typed at "Command:" needs, when it is not a command, to offer "Ask Orthos" (FR-AI-046). */
  VITE_AI_ASK_MIN_WORDS: z.coerce.number().int().min(2).max(20).default(3),
  /** An applied AI proposal undone within this many minutes is logged as undone (research §5.7). */
  VITE_AI_UNDO_WINDOW_MINUTES: z.coerce.number().int().min(1).max(120).default(10),
  /** A new request within this many seconds of a proposal closing is logged as asked again (research §5.7). */
  VITE_AI_REASK_WINDOW_SECONDS: z.coerce.number().int().min(5).max(600).default(60),
  /** How long the preview card waits after a typed value before it rebuilds the ghost, in milliseconds. */
  VITE_AI_PREVIEW_DEBOUNCE_MS: z.coerce.number().int().min(0).max(2_000).default(200),
});

export type AppEnv = z.infer<typeof envSchema>;
type EnvSource = Readonly<Record<string, string | undefined>>;

/** Thrown when configuration is invalid. Lists every failing key at once. */
export class EnvError extends Error {
  readonly issues: readonly string[];

  constructor(issues: readonly string[]) {
    super(`Invalid configuration:\n${issues.map((issue) => `  - ${issue}`).join("\n")}`);
    this.name = "EnvError";
    this.issues = issues;
  }
}

/** Vite inlines only keys it can see statically, so each key is listed. Empty strings count as unset. */
function browserEnvSource(): EnvSource {
  return {
    VITE_MOCK_LATENCY_MS: import.meta.env.VITE_MOCK_LATENCY_MS,
    VITE_DEV_ORG_NAME: import.meta.env.VITE_DEV_ORG_NAME,
    VITE_DEV_ORG_PROJECT_PREFIX: import.meta.env.VITE_DEV_ORG_PROJECT_PREFIX,
    VITE_SUPPORT_EMAIL: import.meta.env.VITE_SUPPORT_EMAIL,
    VITE_SESSION_IDLE_MINUTES: import.meta.env.VITE_SESSION_IDLE_MINUTES,
    VITE_SESSION_ABSOLUTE_DAYS: import.meta.env.VITE_SESSION_ABSOLUTE_DAYS,
    VITE_SESSION_CHECK_SECONDS: import.meta.env.VITE_SESSION_CHECK_SECONDS,
    VITE_IDENTITY_MODE: import.meta.env.VITE_IDENTITY_MODE,
    VITE_IDENTITY_API_PATH: import.meta.env.VITE_IDENTITY_API_PATH,
    VITE_PROJECTS_MODE: import.meta.env.VITE_PROJECTS_MODE,
    VITE_COMMENTS_MODE: import.meta.env.VITE_COMMENTS_MODE,
    VITE_COMMENTS_POLL_SECONDS: import.meta.env.VITE_COMMENTS_POLL_SECONDS,
    VITE_STUDIO_MODE: import.meta.env.VITE_STUDIO_MODE,
    VITE_SITE_BOUNDARY_LAYER: import.meta.env.VITE_SITE_BOUNDARY_LAYER,
    VITE_SITE_DRAWING_UNITS_PER_METRE: import.meta.env.VITE_SITE_DRAWING_UNITS_PER_METRE,
    VITE_SITE_BASEMAP_URL: import.meta.env.VITE_SITE_BASEMAP_URL,
    VITE_SITE_BASEMAP_GLYPHS_URL: import.meta.env.VITE_SITE_BASEMAP_GLYPHS_URL,
    VITE_SITE_BASEMAP_SPRITE_URL: import.meta.env.VITE_SITE_BASEMAP_SPRITE_URL,
    VITE_SITE_INDIA_BOUNDARY_URL: import.meta.env.VITE_SITE_INDIA_BOUNDARY_URL,
    VITE_SITE_SEARCH_INDEX_URL: import.meta.env.VITE_SITE_SEARCH_INDEX_URL,
    VITE_SITE_SEARCH_SOURCE: import.meta.env.VITE_SITE_SEARCH_SOURCE,
    VITE_SITE_SEARCH_FALLBACK: import.meta.env.VITE_SITE_SEARCH_FALLBACK,
    VITE_SITE_SEARCH_DEBOUNCE_MS: import.meta.env.VITE_SITE_SEARCH_DEBOUNCE_MS,
    VITE_SITE_SEARCH_MIN_CHARS: import.meta.env.VITE_SITE_SEARCH_MIN_CHARS,
    VITE_SITE_SEARCH_RESULTS_MAX: import.meta.env.VITE_SITE_SEARCH_RESULTS_MAX,
    VITE_SITE_SEARCH_MAX_SHARDS: import.meta.env.VITE_SITE_SEARCH_MAX_SHARDS,
    VITE_SYNC_MODE: import.meta.env.VITE_SYNC_MODE,
    VITE_SYNC_PATH: import.meta.env.VITE_SYNC_PATH,
    VITE_SYNC_OFFLINE_WINDOW_SECONDS: import.meta.env.VITE_SYNC_OFFLINE_WINDOW_SECONDS,
    VITE_SYNC_OFFLINE_MAX_OPERATIONS: import.meta.env.VITE_SYNC_OFFLINE_MAX_OPERATIONS,
    VITE_DRAFT_HOST: import.meta.env.VITE_DRAFT_HOST,
    VITE_DRAFT_RENDERER: import.meta.env.VITE_DRAFT_RENDERER,
    VITE_DRAFT_RENDER_WORKER: import.meta.env.VITE_DRAFT_RENDER_WORKER,
    VITE_DRAFT_IMAGE_CACHE_MB: import.meta.env.VITE_DRAFT_IMAGE_CACHE_MB,
    VITE_HEIC_JPEG_QUALITY: import.meta.env.VITE_HEIC_JPEG_QUALITY,
    VITE_FILES_MODE: import.meta.env.VITE_FILES_MODE,
    VITE_FILES_API_PATH: import.meta.env.VITE_FILES_API_PATH,
    VITE_LIBRARY_API_PATH: import.meta.env.VITE_LIBRARY_API_PATH,
    VITE_SHIPPED_FONTS_PATH: import.meta.env.VITE_SHIPPED_FONTS_PATH,
    VITE_FILES_MAX_UPLOAD_BYTES: import.meta.env.VITE_FILES_MAX_UPLOAD_BYTES,
    VITE_FILES_MAX_BIG_UPLOAD_BYTES: import.meta.env.VITE_FILES_MAX_BIG_UPLOAD_BYTES,
    VITE_FILES_UPLOAD_PARTS_IN_FLIGHT: import.meta.env.VITE_FILES_UPLOAD_PARTS_IN_FLIGHT,
    VITE_FILES_UPLOAD_PART_RETRIES: import.meta.env.VITE_FILES_UPLOAD_PART_RETRIES,
    VITE_FILES_UPLOAD_RETRY_DELAY_MS: import.meta.env.VITE_FILES_UPLOAD_RETRY_DELAY_MS,
    VITE_FILES_TRASH_RETENTION_DAYS: import.meta.env.VITE_FILES_TRASH_RETENTION_DAYS,
    VITE_BENCH: import.meta.env.VITE_BENCH,
    VITE_AI_MODE: import.meta.env.VITE_AI_MODE,
    VITE_PLAN_INTAKE: import.meta.env.VITE_PLAN_INTAKE,
    VITE_AI_API_PATH: import.meta.env.VITE_AI_API_PATH,
    VITE_AI_MAX_SELECTION_OBJECTS: import.meta.env.VITE_AI_MAX_SELECTION_OBJECTS,
    VITE_AI_MAX_TOOL_ROUNDS: import.meta.env.VITE_AI_MAX_TOOL_ROUNDS,
    VITE_AI_MAX_RESULT_CHARS: import.meta.env.VITE_AI_MAX_RESULT_CHARS,
    VITE_AI_MAX_CHANGES: import.meta.env.VITE_AI_MAX_CHANGES,
    VITE_AI_MAX_HISTORY_MESSAGES: import.meta.env.VITE_AI_MAX_HISTORY_MESSAGES,
    VITE_AI_COMMAND_LINES: import.meta.env.VITE_AI_COMMAND_LINES,
    VITE_AI_TITLE_CHARS: import.meta.env.VITE_AI_TITLE_CHARS,
    VITE_AI_ASK_MIN_WORDS: import.meta.env.VITE_AI_ASK_MIN_WORDS,
    VITE_AI_UNDO_WINDOW_MINUTES: import.meta.env.VITE_AI_UNDO_WINDOW_MINUTES,
    VITE_AI_REASK_WINDOW_SECONDS: import.meta.env.VITE_AI_REASK_WINDOW_SECONDS,
    VITE_AI_PREVIEW_DEBOUNCE_MS: import.meta.env.VITE_AI_PREVIEW_DEBOUNCE_MS,
  };
}

/** Parse once at startup and pass the result down; throws `EnvError` listing every bad key. */
export function loadEnv(source: EnvSource = browserEnvSource()): AppEnv {
  const present = Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== undefined && value !== ""),
  );
  const result = envSchema
    .refine((env) => env.VITE_PROJECTS_MODE === "mock" || env.VITE_IDENTITY_MODE === "live", {
      message:
        "live projects need VITE_IDENTITY_MODE=live, which signs people in to the service that holds them",
      path: ["VITE_PROJECTS_MODE"],
    })
    .refine((env) => env.VITE_COMMENTS_MODE === "mock" || env.VITE_PROJECTS_MODE === "live", {
      message:
        "live comments need VITE_PROJECTS_MODE=live, which holds the projects they belong to",
      path: ["VITE_COMMENTS_MODE"],
    })
    .refine((env) => env.VITE_AI_MODE === "off" || env.VITE_PROJECTS_MODE === "live", {
      message:
        "live AI needs VITE_PROJECTS_MODE=live: the ai service checks each project's AI switches",
      path: ["VITE_AI_MODE"],
    })
    .refine((env) => env.VITE_STUDIO_MODE === "mock" || env.VITE_PROJECTS_MODE === "live", {
      message:
        "live Studio needs VITE_PROJECTS_MODE=live, which holds the projects its stages belong to",
      path: ["VITE_STUDIO_MODE"],
    })
    .safeParse(present);
  if (!result.success) {
    throw new EnvError(
      result.error.issues.map((issue) => `${issue.path.map(String).join(".")}: ${issue.message}`),
    );
  }
  return result.data;
}
